import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarDays,
  ClipboardList,
  Users,
  Receipt,
  LogOut,
  Snowflake,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/office", end: true, icon: LayoutDashboard, label: "Dashboard", tid: "nav-dashboard" },
  { to: "/office/schedule", icon: CalendarDays, label: "Schedule", tid: "nav-schedule" },
  { to: "/office/jobs", icon: ClipboardList, label: "Jobs", tid: "nav-jobs" },
  { to: "/office/clients", icon: Users, label: "Clients & Assets", tid: "nav-clients" },
  { to: "/office/invoices", icon: Receipt, label: "Invoices", tid: "nav-invoices" },
];

const OWNER_NAV = [
  { to: "/office/users", icon: ShieldCheck, label: "Users", tid: "nav-users" },
];

export default function OfficeLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-[#F5F6FA]">
      {/* Sidebar */}
      <aside
        className="hidden md:flex md:flex-col w-64 bg-[#181854] text-white flex-shrink-0"
        data-testid="office-sidebar"
      >
        {/* Logo panel — dark navy logo sits on WHITE per brand rules */}
        <div className="p-4">
          <div className="bg-white rounded-xl px-3 py-3 flex items-center justify-center shadow-sm">
            <img
              src="/brand/gt-logo.webp"
              alt="GT Refrigeration & Aircon Services"
              className="h-10 w-auto object-contain"
              data-testid="office-sidebar-logo"
            />
          </div>
          <div className="mt-3 px-1">
            <p className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
              <Snowflake size={11} strokeWidth={2.4} />
              Commercial Cooling
            </p>
            <p className="text-[11px] text-white/70">Performance Proven.</p>
          </div>
        </div>

        <nav className="flex-1 px-3 pt-2 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-[#101038] text-white border-l-2 border-[#4F9CE0]"
                    : "text-white/80 hover:bg-[#101038] hover:text-white"
                }`
              }
            >
              <item.icon size={18} strokeWidth={2} />
              <span>{item.label}</span>
            </NavLink>
          ))}
          {user?.role === "owner" && (
            <>
              <div className="mt-4 mb-1 px-3 text-[10px] uppercase tracking-widest text-white/40 font-semibold">
                Admin
              </div>
              {OWNER_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.tid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      isActive
                        ? "bg-[#101038] text-white border-l-2 border-[#4F9CE0]"
                        : "text-white/80 hover:bg-[#101038] hover:text-white"
                    }`
                  }
                >
                  <item.icon size={18} strokeWidth={2} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 text-xs text-white/60">
            Signed in as
            <div className="text-white text-sm font-medium mt-0.5">
              {user?.name}
            </div>
            <div className="text-white/50 capitalize">{user?.role}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-[#101038] hover:text-white transition-colors duration-200"
          >
            <LogOut size={16} /> Log out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-[#E4E6EF] flex items-center justify-between px-6 sticky top-0 z-10">
          <div>
            <h1 className="text-lg font-semibold text-[#181854]">GT Field OS</h1>
            <p className="text-xs text-[#6A6E85] hidden sm:block">
              Commercial Cooling. Performance Proven.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/office/jobs?new=1")}
              data-testid="topbar-new-job"
              className="hidden sm:inline-flex items-center gap-2 bg-[#181854] hover:bg-[#101038] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
            >
              + New Job
            </button>
            <div className="w-9 h-9 rounded-full bg-[#ECEDF6] flex items-center justify-center text-[#181854] font-semibold text-sm">
              {user?.name?.split(" ").map((n) => n[0]).join("").slice(0, 2) || "GT"}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
